import React from 'react'

import { memoize } from '~/src/Lib/Utils'
import Tooltip from '~/src/UI/Shared/Tooltip'

import Person from '../../Widgets/Person'

const WORD = 'WORD'
const MENTION = 'MENTION'
const EMAIL = 'EMAIL'
const URL = 'URL'
const BREAK = 'BREAK'

export const MENTION_CONFIG = {
  type: MENTION,
  parsePattern: /@\[([^\]]+)\]\((\d+)\)$/,
  splitPattern: /(@\[[^\]]+\]\(\d+\))/,
  [Symbol.split](str) {
    return str.split(this.splitPattern)
  },
  parse(str) {
    const [, name, id] = str.match(this.parsePattern) ?? []
    return name && id ? { type: this.type, name, id } : str
  }
}

export const EMAIL_CONFIG = {
  type: EMAIL,
  parsePattern: /^[a-z][\w.+-]+@[\w-]+(\.[\w-]+)+$/i,
  splitPattern: /([a-z][\w.+-]+@[\w-]+(\.[\w-]+)+)/i,
  [Symbol.split](str) {
    return str.split(this.splitPattern).filter(s => !s.startsWith('.'))
  },
  parse(str) {
    const [email] = str.match(this.parsePattern) ?? []
    return email ? { type: this.type, email } : str
  }
}

export const URL_CONFIG = {
  type: URL,
  parsePattern: /https?:\/\/[\w-]+(\.[\w-]+)+\/?.*/,
  splitPattern: /(https?:\/\/[^\s]+)/,
  [Symbol.split](str) {
    return str.split(this.splitPattern)
  },
  parse(str) {
    const [url] = str.match(this.parsePattern) ?? []
    return url ? { type: this.type, url } : str
  }
}

export const BREAK_CONFIG = {
  type: BREAK,
  splitPattern: /(\n)/,
  parsePattern: /^\n$/,
  [Symbol.split](str) {
    return str.split(this.splitPattern)
  },
  parse(str) {
    return str ? { type: this.type } : null
  }
}

export const WORD_CONFIG = {
  type: WORD,
  splitPattern: /([\s]+(?![A-Z0-9]))/i,
  [Symbol.split](str) {
    return str.split(MENTION_CONFIG.splitPattern)
      .flatMap(s => {
        if (MENTION_CONFIG.parsePattern.test(s)) return s
        if (EMAIL_CONFIG.splitPattern.test(s)) return s.split(EMAIL_CONFIG)
        if (URL_CONFIG.splitPattern.test(s)) return s.split(URL_CONFIG)
        if (BREAK_CONFIG.splitPattern.test(s)) return s.split(BREAK_CONFIG)
        return s.split(this.splitPattern)
      }).filter(Boolean)
  },
  parse(str) {
    return str
  }
}

const PARSEABLE_PATTERNS = [
  WORD_CONFIG,
  MENTION_CONFIG,
  EMAIL_CONFIG,
  URL_CONFIG,
  BREAK_CONFIG,
]

const FORMATTERS = {
  [MENTION]: ({ id, name }, classes) => (
    <Tooltip key={id} title={<Person membership={id} />}>
      <span className={classes.mention}>@{name}</span>
    </Tooltip>
  ),
  [EMAIL]: ({ email }, classes) => (
    <a key={email} className={classes.email} href={`mailto:${email}`}>
      {email}
    </a>
  ),
  [URL]: ({ url }, classes) => (
    <a key={url} className={classes.url} href={url} rel="noreferrer" target="_blank">
      {url}
    </a>
  ),
  [BREAK]: () => <br />,
}

export const parseText = memoize(text => PARSEABLE_PATTERNS.reduce((acc, pattern) => {
  if (Array.isArray(acc)) {
    return acc.flatMap(part => {
      if (typeof part !== 'string') return part
      return part.match(pattern.splitPattern)
        ? part.split(pattern).map(p => {
          if (pattern.parsePattern.test(p)) {
            return pattern.parse(p)
          }
          return p
        })
        : part
    })
  }
  return acc.split(pattern)
}, text).filter(Boolean))

export const formatPart = (thing, classes) => (
  typeof FORMATTERS[thing.type] === 'function'
    ? FORMATTERS[thing.type](thing, classes)
    : null
)
